import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, ArrowRight, Mail, Phone, Package, Inbox, AlertCircle, Check, Layers, Palette, FileEdit, Receipt, Wallet, Trash2 } from 'lucide-react';
import { adminApi, formatApiError } from '../utils/adminApi';
import ProductsTab from './admin/ProductsTab';
import CatalogTab from './admin/CatalogTab';
import PagesTab from './admin/PagesTab';
import OrdersTab from './admin/OrdersTab';
import BillingTab from './admin/BillingTab';
import ExpenseTab from './admin/ExpenseTab';
import SEO from '../components/SEO';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [inquiryFilter, setInquiryFilter] = useState('all');
  const [orderCount, setOrderCount] = useState(0);
  const [unseenOrders, setUnseenOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inq, stats] = await Promise.all([
        adminApi.listInquiries(),
        adminApi.orderStats(),
      ]);
      setInquiries(inq);
      setOrderCount(stats.total || 0);
      setUnseenOrders(stats.unseen || 0);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load data.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    adminApi.me()
      .then((u) => {
        setUser(u);
        refresh();
      })
      .catch(() => navigate('/admin/login', { replace: true }));
  }, [navigate, refresh]);

  const handleLogout = () => {
    adminApi.logout();
    navigate('/admin/login', { replace: true });
  };

  const handleConvert = async (id) => {
    try {
      await adminApi.convertInquiry(id);
      showToast('Inquiry converted to order');
      refresh();
    } catch (err) {
      setError(formatApiError(err, 'Conversion failed.'));
    }
  };

  const handleInquiryStatus = async (id, status) => {
    try {
      await adminApi.updateInquiry(id, { status });
      refresh();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDeleteInquiry = async (id, name) => {
    if (!window.confirm(`Permanently delete the inquiry from "${name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteInquiry(id);
      showToast('Inquiry deleted');
      refresh();
    } catch (err) {
      setError(formatApiError(err, 'Could not delete inquiry.'));
    }
  };

  if (!user) {
    return (
      <div className="admin-loading">
        <p className="body-large">Loading atelier…</p>
      </div>
    );
  }

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" path="/admin" noIndex />
      {/* Header */}
      <header className="admin-header">
        <div className="luxury-container admin-header-inner">
          <div>
            <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>Atelier</p>
            <h1 className="heading-1" style={{ marginTop: 4 }}>Silk &amp; Sentiment</h1>
          </div>
          <div className="admin-header-meta">
            <span className="body-small" data-testid="admin-user-email">{user.email}</span>
            <button
              className="btn-secondary admin-icon-btn"
              onClick={refresh}
              data-testid="admin-refresh-btn"
              title="Refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              className="btn-secondary admin-icon-btn"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              title="Logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <div className="luxury-container">
          <div className="admin-tab-buttons">
            <button
              className={`admin-tab-btn ${tab === 'inquiries' ? 'active' : ''}`}
              onClick={() => setTab('inquiries')}
              data-testid="admin-tab-inquiries"
            >
              <Inbox size={14} /> Inquiries
              <span className="admin-tab-count">{inquiries.length}</span>
            </button>
            <button
              className={`admin-tab-btn ${tab === 'orders' ? 'active' : ''}`}
              onClick={() => setTab('orders')}
              data-testid="admin-tab-orders"
            >
              <Package size={14} /> Orders
              <span className="admin-tab-count">{orderCount}</span>
              {unseenOrders > 0 && (
                <span className="admin-tab-new-badge" data-testid="admin-tab-orders-new" title={`${unseenOrders} new`}>
                  {unseenOrders}
                </span>
              )}
            </button>
            <button
              className={`admin-tab-btn ${tab === 'products' ? 'active' : ''}`}
              onClick={() => setTab('products')}
              data-testid="admin-tab-products"
            >
              <Layers size={14} /> Products
            </button>
            <button
              className={`admin-tab-btn ${tab === 'catalog' ? 'active' : ''}`}
              onClick={() => setTab('catalog')}
              data-testid="admin-tab-catalog"
            >
              <Palette size={14} /> Catalog
            </button>
            <button
              className={`admin-tab-btn ${tab === 'pages' ? 'active' : ''}`}
              onClick={() => setTab('pages')}
              data-testid="admin-tab-pages"
            >
              <FileEdit size={14} /> Pages
            </button>
            <button
              className={`admin-tab-btn ${tab === 'billing' ? 'active' : ''}`}
              onClick={() => setTab('billing')}
              data-testid="admin-tab-billing"
            >
              <Receipt size={14} /> Billing
            </button>
            <button
              className={`admin-tab-btn ${tab === 'expenses' ? 'active' : ''}`}
              onClick={() => setTab('expenses')}
              data-testid="admin-tab-expenses"
            >
              <Wallet size={14} /> Expenses
            </button>
          </div>
        </div>
      </div>

      <main className="luxury-container admin-main">
        {error && (
          <div className="admin-error" data-testid="admin-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {tab === 'inquiries' && (
          <>
            {loading && <p className="body-regular">Loading…</p>}
            {!loading && (
              <InquiriesPanel
                items={inquiries}
                filter={inquiryFilter}
                onFilterChange={setInquiryFilter}
                onConvert={handleConvert}
                onStatus={handleInquiryStatus}
                onDelete={handleDeleteInquiry}
              />
            )}
          </>
        )}

        {tab === 'orders' && (
          <OrdersTab
            onToast={showToast}
            onUnseenChange={(n) => {
              setUnseenOrders(n);
              // Also refresh inquiries-based count when orders change
              adminApi.orderStats().then((s) => setOrderCount(s.total || 0)).catch(() => {});
            }}
          />
        )}

        {tab === 'products' && <ProductsTab onToast={showToast} />}
        {tab === 'catalog' && <CatalogTab onToast={showToast} />}
        {tab === 'pages' && <PagesTab onToast={showToast} />}
        {tab === 'billing' && <BillingTab onToast={showToast} />}
        {tab === 'expenses' && <ExpenseTab onToast={showToast} />}
      </main>

      {toast && (
        <div className="admin-toast" data-testid="admin-toast">
          <Check size={14} /> {toast}
        </div>
      )}
    </div>
  );
};

const InquiriesPanel = ({ items, filter, onFilterChange, onConvert, onStatus, onDelete }) => {
  const counts = items.reduce(
    (acc, i) => {
      const k = i.status || 'new';
      acc[k] = (acc[k] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, new: 0, contacted: 0, completed: 0 }
  );

  const filtered = filter === 'all' ? items : items.filter((i) => (i.status || 'new') === filter);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'new', label: 'New' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'completed', label: 'Completed' },
  ];

  return (
    <>
      <div className="admin-section-actions">
        <div className="admin-filter-group" data-testid="inquiries-filter-group">
          {filters.map((f) => (
            <button
              key={f.id}
              className={`admin-filter-pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => onFilterChange(f.id)}
              data-testid={`inquiry-filter-${f.id}`}
            >
              {f.label} <span style={{ marginLeft: 6, opacity: 0.6 }}>{counts[f.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {!filtered.length ? (
        <p className="body-regular admin-empty">No inquiries in this view.</p>
      ) : (
        <InquiriesTable items={filtered} onConvert={onConvert} onStatus={onStatus} onDelete={onDelete} />
      )}
    </>
  );
};

const InquiriesTable = ({ items, onConvert, onStatus, onDelete }) => {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table" data-testid="admin-inquiries-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Contact</th>
            <th>Piece</th>
            <th>Message</th>
            <th>Received</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} data-testid={`inquiry-row-${i.id}`}>
              <td>
                <strong>{i.name}</strong>
                <div className="admin-cell-sub">{i.source?.replace('_', ' ')}</div>
              </td>
              <td>
                <a href={`mailto:${i.email}`} className="admin-link"><Mail size={12} /> {i.email}</a>
                <div className="admin-cell-sub"><Phone size={12} /> {i.phone}</div>
              </td>
              <td>{i.pieceName || <em style={{ color: 'var(--text-light)' }}>—</em>}</td>
              <td className="admin-cell-message" title={i.message}>{i.message}</td>
              <td className="admin-cell-meta">{formatDate(i.createdAt)}</td>
              <td>
                <select
                  value={i.status || 'new'}
                  onChange={(e) => onStatus(i.id, e.target.value)}
                  className="admin-select"
                  data-testid={`inquiry-status-${i.id}`}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                </select>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {i.convertedOrderId ? (
                    <span className="admin-pill admin-pill-success">Converted</span>
                  ) : (
                    <button
                      className="btn-secondary admin-row-btn"
                      onClick={() => onConvert(i.id)}
                      data-testid={`inquiry-convert-${i.id}`}
                    >
                      Convert <ArrowRight size={12} />
                    </button>
                  )}
                  <button
                    className="admin-icon-only"
                    onClick={() => onDelete(i.id, i.name)}
                    title="Delete inquiry"
                    data-testid={`inquiry-delete-${i.id}`}
                    style={{ color: '#a85a2c' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminDashboardPage;
